import React, { Component } from 'react';
import PropTypes from 'prop-types';
import segmentize from 'segmentize';
import { Select } from '@components/common';
import classNames from 'classnames';
import { isEqual, isUndefined } from 'lodash';
import { Button } from 'react-bootstrap';

const DISPLAY_COUNT_OPTIONS = [
	{ value : 25, label : '25' },
	{ value : 50, label : '50' },
	{ value : 100, label : '100' },
	{ value : 500, label : '500' },
];

const buildState = ({ displayCount, totalCount, pageNumber: page }) => {
	const pages = Math.ceil(totalCount / displayCount);
	const segments = segmentize({
		pages,
		page,
		beginPages : 2,
		endPages   : 2,
		sidePages  : 2,
	});

	return {
		pages,
		segments,
	};
};

class Pagination extends Component {
	constructor(props) {
		super(props);

		const {
			displayCount,
			totalCount,
			pageNumber,
		} = props;

		this.state = buildState({ displayCount, totalCount, pageNumber });
	}

	static getDerivedStateFromProps({ displayCount, totalCount, pageNumber }, lastState) {
		if (
			displayCount !== lastState.displayCount
			|| totalCount !== lastState.totalCount
			|| pageNumber !== lastState.pageNumber
		) return buildState({ displayCount, totalCount, pageNumber });

		return null;
	}

	shouldComponentUpdate = ({ disabled }, { pages, segments }) => {
		const lastProps = this.props;
		const lastState = this.state;

		return (
			!isEqual(disabled, lastProps.disabled)
			|| !isEqual(pages, lastState.pages)
			|| !isEqual(segments, lastState.segments)
		);
	};

	onPaginate = (nextPage) => () => {
		const {
			pageNumber,
			disabled,
			onPaginate,
		} = this.props;
		const { pages } = this.state;

		if (disabled || nextPage < 1 || nextPage > pages || pageNumber === nextPage) return;

		onPaginate(nextPage);
	};

	onDisplayCountChange = ({ value }) => {
		const { disabled, onDisplayCountChange } = this.props;

		if (disabled) return;

		onDisplayCountChange(value);
	};

	render() {
		const {
			displayCount,
			totalCount,
			pageNumber,
			onDisplayCountChange,
			className,
			disabled,
		} = this.props;
		const {
			pages,
			segments,
		} = this.state;

		return (
			<If condition={!((totalCount <= displayCount && isUndefined(onDisplayCountChange)) || totalCount === 0)}>
				<div className={classNames('table-pagination flex', className)}>
					<div className="flex-auto">
						<If condition={!isUndefined(onDisplayCountChange)}>
							<div className="flex flex-align-items-center">
								<div className="margin-right-10">Show</div>
								<Select
									value={displayCount}
									name="displayCount"
									onChange={this.onDisplayCountChange}
									options={DISPLAY_COUNT_OPTIONS}
									menuPlacement="top"
									isDisabled={disabled}
								/>
							</div>
						</If>
					</div>
					<div className="flex flex-align-items-center">
						<div className="flex-1">
							<Button
								bsStyle="link"
								onClick={this.onPaginate(pageNumber - 1)}
								disabled={disabled || pageNumber === 1}
							>
								<i className="rkf rkf-chevron-left font-size-36" />
							</Button>
						</div>

						<If condition={segments.centerPage.length}>
							<For
								each="segment"
								index="index"
								of={segments.centerPage}
							>
								<div
									key={`${segment}-page`}
									className="page-number"
								>
									{segment}
								</div>
							</For>
						</If>
						<div className="flex-1">
							<Button
								bsStyle="link"
								onClick={this.onPaginate(pageNumber + 1)}
								disabled={disabled || pageNumber === pages}
							>
								<i className="rkf rkf-chevron-right font-size-36" />
							</Button>
						</div>
						<div className="flex-3 font-size-15 text-nowrap">
							<span className="margin-left-5 margin-right-10">of</span>
							<span>{pages}</span>
						</div>
					</div>
				</div>
			</If>
		);
	}
}

Pagination.propTypes = {
	totalCount           : PropTypes.number.isRequired,
	pageNumber           : PropTypes.number.isRequired,
	onPaginate           : PropTypes.func.isRequired,
	displayCount         : PropTypes.number,
	onDisplayCountChange : PropTypes.func,
	className            : PropTypes.string,
	disabled             : PropTypes.bool,
};

Pagination.defaultProps = {
	className            : undefined,
	disabled             : false,
	onDisplayCountChange : undefined,
	displayCount         : 25,
};

export default Pagination;
